import React, { useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  BooleanField,
  Filter,
  TextInput,
  BooleanInput,
  DateField,
  useDataProvider,
  useListContext,
} from 'react-admin';
import { Card, CardContent, Typography, Toolbar, Chip, FormControl, Select, MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import TodayIcon from '@mui/icons-material/Today';

const ValidatedUserIdInput = ({ onChange, helperText, ...props }) => {
  const { setFilters, filterValues } = useListContext(); // useListContext-с filter-ийг удирдана
  const [error, setError] = useState(false);
  const [localHelper, setLocalHelper] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;

    if (value === '' || /^\d*$/.test(value)) {
      setError(false);
      setLocalHelper('');
      const newFilters = { ...filterValues, userId: value || undefined };
      setFilters(newFilters, null);
    } else {
      setError(true);
      setLocalHelper('Хэрэглэгчийн ID зөвхөн тооноос бүрдэх ёстой');
      const newFilters = { ...filterValues };
      delete newFilters.userId;
      setFilters(newFilters, null);
    }
  };

  return (
    <TextInput
      {...props}
      onChange={handleChange}
      error={error}
      helperText={error ? localHelper : helperText}
    />
  );
};

// Phone Number Input (numbers + + - ( ) space)
const ValidatedPhoneInput = ({ onChange, helperText, ...props }) => {
  const [error, setError] = useState(false);
  const [localHelper, setLocalHelper] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^[0-9+\-\s()]*$/.test(value)) {
      setError(false);
      setLocalHelper('');
      onChange && onChange(value);
    } else {
      setError(true);
      setLocalHelper('Утасны дугаар зөв форматтай байх ёстой');
    }
  };

  return (
    <TextInput
      {...props}
      onChange={handleChange}
      error={error}
      helperText={error ? localHelper : helperText}
    />
  );
};

// --- Filter ---
const FreeTrialFilter = (props) => (
  <Filter {...props}>
    <ValidatedUserIdInput label="User ID" source="userId" />
    <ValidatedPhoneInput label="Phone number" source="phoneNumber" />
    <BooleanInput label="Is Unitel Trial" source="isUnitelTrial" />
  </Filter>
);

// --- Today Stats Card ---
const FreeTrialStats = () => {
  const dataProvider = useDataProvider();
  const [todayCount, setTodayCount] = useState(0);

  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

    dataProvider
      .getList('freeTrials', { pagination: { page: 1, perPage: 1 }, sort: { field: 'startAt', order: 'DESC' }, filter: { startFrom: start.toISOString(), startTo: end.toISOString() } })
      .then((res) => setTodayCount(res.todayCount || 0))
      .catch(() => setTodayCount(0));
  }, [dataProvider]);

  return (
    <Card style={{ margin: '8px 16px', backgroundColor: '#f8fafc', border: '1px solid #eef2f7' }}>
      <CardContent style={{ padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <TodayIcon style={{ fontSize: 18, color: '#64748b' }} />
        <Typography variant="body2" style={{ color: '#475569' }}>Өнөөдөр</Typography>
        <Chip size="small" label={todayCount} style={{ backgroundColor: '#e3f2fd' }} />
        <Typography variant="body2" style={{ color: '#475569' }}>үнэгүй эрх идэвхжлээ</Typography>
      </CardContent>
    </Card>
  );
};

// --- Actions Toolbar ---
const FreeTrialActions = (props) => {
  const { filters, resource, showFilter, displayedFilters, filterValues } = props;
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <Toolbar style={{ padding: 0 }}>
        {filters && React.cloneElement(filters, { resource, showFilter, displayedFilters, filterValues, context: 'button' })}
      </Toolbar>
      <FreeTrialStats />
    </div>
  );
};

// --- Status Filter in Header ---
const StatusFilterHeader = () => {
  const { filterValues, setFilters } = useListContext();
  const current = filterValues.expired === undefined ? '' : filterValues.expired ? 'expired' : 'active';

  const handleChange = (e) => {
    const value = e.target.value;
    const newFilters = { ...filterValues };
    if (value === '') delete newFilters.expired;
    else newFilters.expired = value === 'expired';
    setFilters(newFilters);
  };

  return (
    <FormControl variant="standard" size="small">
      <Select value={current} onChange={handleChange} displayEmpty disableUnderline style={{ fontSize: 13, fontWeight: 500 }}>
        <MenuItem value="">Төлөв</MenuItem>
        <MenuItem value="active">Идэвхтэй</MenuItem>
        <MenuItem value="expired">Дууссан</MenuItem>
      </Select>
    </FormControl>
  );
};

// --- FreeTrial List ---
export const FreeTrialList = (props) => (
  <List {...props} filters={<FreeTrialFilter />} actions={<FreeTrialActions />} sort={{ field: 'startAt', order: 'DESC' }} bulkActionButtons={false} perPage={50}>
    <Datagrid>
      <TextField source="id" sortable />
      <FunctionField
        label="Хэрэглэгч"
        render={(r) => {
          const fullName = `${r?.user?.firstName || ''} ${r?.user?.lastName || ''}`.trim();
          const title = fullName || r?.user?.email || r?.user?.phoneNumber || `User #${r.userId}`;
          return <Link to={`/users/${r.userId}/show`} style={{ textDecoration: 'none' }}>{title}</Link>;
        }}
      />
      <FunctionField label={<StatusFilterHeader />} render={(r) => r.expired ? <Chip size="small" label="Дууссан" style={{ backgroundColor: '#ffe2e0' }} /> : <Chip size="small" label="Идэвхтэй" style={{ backgroundColor: '#e5f6e5' }} />} />
      <FunctionField label="Төлөвлөгөө" render={(r) => r?.plan?.dayOfTrial ? `${r.plan.dayOfTrial} өдөр` : '-'} />
      <TextField source="daysRemaining" label="Үлдсэн өдөр" />
      <DateField source="startAt" label="Эхлэх" showTime />
      <DateField source="expireAt" label="Дуусах" showTime />
      <BooleanField source="isUnitelTrial" label="Юнител" />
      <TextField source="phoneNumber" label="Утас" sortable />
      <BooleanField source="autoSubscriptionScheduled" label="Автомат сунгалт" />
    </Datagrid>
  </List>
);

export default FreeTrialList;
